import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PresenceAvatar from "./PresenceAvatar";
import NameTooltip from "../ui/NameTooltip";
import useAuth from "../../hooks/useAuth";
import { isAdmin } from "../../utils/permissions";
import { usePolling, useLivePresence } from "./usePresence";
import { presenceService } from "../../api/presenceService";
import { STATUS_META, formatClock, readLateAfter, statusOf, todayIn, todaySummary } from "./presence";


/**
 * Dashboard strip (whole team): who is on Discord today — working, idle and
 * checked out (shown offline) — team focus, average check-in and late count.
 * Same numbers as the Team Presence page.
 */
const LiveOnDiscord = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const admin = isAdmin(auth?.employee);
  const today = todayIn();
  const { data, loading, error } = usePolling(() => presenceService.getPresence({ from: today, to: today }), [today]);
  const { live } = useLivePresence(true);
  const lateAfter = readLateAfter();
  const rows = useMemo(() => data?.rows || [], [data]);
  const sum = todaySummary(rows, live, lateAfter);
  // Everyone who checked in today: working first, then idle, then checked out (offline).
  const rank = { working: 0, idle: 1, out: 2 };
  const online = rows
    .map((r) => ({ r, s: statusOf(r, live) }))
    .sort((a, b) => rank[a.s] - rank[b.s]);

  if (error) return null; // presence not configured: no strip

  return (
    <section className="ws-in card !py-[18px] !px-[22px] mb-6 flex flex-wrap items-center gap-[22px]" style={{ "--ws-delay": "240ms" }}>
      {/* Title + counts */}
      <div className="min-w-0">
        <h3 className="flex items-center gap-2 font-display text-[17px] font-semibold text-ink whitespace-nowrap">
          <span className="w-[7px] h-[7px] rounded-full bg-ok animate-pulse" style={{ boxShadow: "0 0 8px var(--green)" }} />
          Live on Discord
        </h3>
        <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[12.5px] text-muted">
          {[
            ["working", sum.working, "Working"],
            ["idle", sum.idle, "Idle"],
            ["out", sum.out, "Checked out"],
          ].map(([k, n, label]) => (
            <span key={k} className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_META[k].dot }} />
              <b className="text-ink font-semibold">{loading && !data ? "…" : n}</b> {label}
            </span>
          ))}
        </p>
      </div>

      {/* Avatar stack */}
      <div className="flex items-center flex-1 min-w-[120px]">
        {online.length === 0 ? (
          <span className="text-[13px] text-faint">{loading && !data ? "Loading…" : "No one has checked in yet today"}</span>
        ) : (
          <div className="flex flex-wrap items-center gap-y-2 pl-2">
            {online.map(({ r, s }) => (
              <NameTooltip key={r.userId} name={r.displayName} detail={s === "out" ? "Offline" : STATUS_META[s].label} className="-ml-2">
                <span
                  className={`rounded-full transition-transform duration-200 hover:-translate-y-[3px] ${s === "out" ? "opacity-50 grayscale" : ""}`}
                  style={{ border: "2.5px solid var(--panel-solid)" }}
                >
                  <PresenceAvatar name={r.displayName} size={38} status={s} />
                </span>
              </NameTooltip>
            ))}
          </div>
        )}
      </div>

      {/* Focus · check-in · late · button (groups wrap as wholes) */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
        <div className="flex items-center gap-2.5 whitespace-nowrap">
          <span
            className="relative w-11 h-11 rounded-full shrink-0"
            style={{ background: `conic-gradient(var(--green) 0 ${sum.focusPct}%, var(--amber) ${sum.focusPct}% 100%)` }}
          >
            <span className="absolute inset-[6px] rounded-full bg-panel-solid flex items-center justify-center font-mono text-[10.5px] font-semibold text-ink">
              {sum.focusPct}%
            </span>
          </span>
          <span className="text-[12px] text-muted">Team focus</span>
        </div>
        <span className="hidden sm:block w-px h-9 bg-line" />
        <div className="whitespace-nowrap">
          <p className="text-[11.5px] text-faint">Avg check-in</p>
          <p className="font-mono text-[14px] text-ink">{sum.avgCheckIn === null ? "—" : formatClock(sum.avgCheckIn)}</p>
        </div>
        <div className="whitespace-nowrap">
          <p className="text-[11.5px] text-faint">Late today</p>
          <p className={`font-mono text-[14px] ${sum.late ? "text-danger" : "text-ink"}`}>{sum.late}</p>
        </div>
        {admin && (
          <button type="button" onClick={() => navigate("/team-presence")} className="btn-ghost !h-9 text-[13px]">
            Open Team Presence <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </section>
  );
};

export default LiveOnDiscord;
